import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon path (CRA bundling)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const numberedIcon = (n, color = '#002FA7') => L.divIcon({
  className: 'route-numbered-marker',
  html: `<div style="
    width:30px;height:30px;border-radius:50%;
    background:${color};color:#fff;
    display:flex;align-items:center;justify-content:center;
    font-weight:700;font-size:13px;
    border:2px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,.35);
    font-family:Work Sans, sans-serif;
  ">${n}</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const legMilesIcon = (miles) => L.divIcon({
  className: 'route-leg-miles',
  html: `<div style="
    padding:3px 9px;border-radius:999px;
    background:rgba(255,255,255,0.96);color:#0F172A;
    font-weight:700;font-size:11px;letter-spacing:0.02em;
    border:1px solid #CBD5E1;box-shadow:0 1px 4px rgba(15,23,42,.18);
    font-family:IBM Plex Sans, sans-serif;
    white-space:nowrap;
  ">${miles} mi</div>`,
  iconSize: [60, 22],
  iconAnchor: [30, 11],
});

// Component to auto-fit bounds when stops change
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 7);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 9 });
  }, [points, map]);
  return null;
};

const RoutePreviewMap = ({ stops, breakdown = [], height = 280 }) => {
  const mapRef = useRef(null);

  const resolved = (stops || []).filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number');

  if (resolved.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
        style={{ height, fontFamily: 'IBM Plex Sans, sans-serif' }}
        data-testid="route-map-empty"
      >
        Add at least one pickup and one delivery stop to preview the route.
      </div>
    );
  }

  const points = resolved.map((s) => [s.lat, s.lng]);
  const center = points[0];

  // Map original stop index → position in resolved[] array, so leg badges line up
  // even when some stops failed to geocode.
  const indexMap = {};
  (stops || []).forEach((s, i) => {
    if (typeof s.lat === 'number' && typeof s.lng === 'number') {
      indexMap[i] = resolved.findIndex((r) => r === s);
    }
  });

  const legMarkers = (breakdown || [])
    .map((leg, i) => {
      const ai = indexMap[leg.from_idx];
      const bi = indexMap[leg.to_idx];
      if (ai == null || bi == null || !resolved[ai] || !resolved[bi]) return null;
      const lat = (resolved[ai].lat + resolved[bi].lat) / 2;
      const lng = (resolved[ai].lng + resolved[bi].lng) / 2;
      return { id: `leg-${i}`, lat, lng, miles: Math.round(leg.miles) };
    })
    .filter(Boolean);

  return (
    <div
      data-testid="route-preview-map"
      className="rounded-lg overflow-hidden border border-slate-200"
      style={{ height }}
    >
      <MapContainer
        center={center}
        zoom={5}
        ref={mapRef}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {points.length >= 2 && (
          <Polyline
            positions={points}
            pathOptions={{ color: '#002FA7', weight: 3, opacity: 0.8, dashArray: '6 6' }}
          />
        )}
        {resolved.map((s, i) => {
          const isPickup = s.kind === 'pickup';
          const color = isPickup ? '#002FA7' : '#34C759';
          return (
            <Marker key={i} position={[s.lat, s.lng]} icon={numberedIcon(i + 1, color)}>
              <Popup>
                <div style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: 12 }}>
                  <strong style={{ color }}>{isPickup ? 'PICKUP' : 'DELIVERY'} #{i + 1}</strong>
                  <br />
                  {s.city}, {s.state}
                </div>
              </Popup>
            </Marker>
          );
        })}
        {legMarkers.map((m) => (
          <Marker
            key={m.id}
            position={[m.lat, m.lng]}
            icon={legMilesIcon(m.miles)}
            interactive={false}
            keyboard={false}
          />
        ))}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
};

export default RoutePreviewMap;
